import {
  Archive,
  Box1,
  Calendar,
  Chart,
  ClipboardText,
  Document,
  DocumentUpload,
  Element3,
  Monitor,
  People,
  Personalcard,
  ScanBarcode,
  Setting2,
  ShoppingCart,
  StatusUp,
  Task,
  TickCircle,
  CloseCircle,
  Cpu,
} from 'iconsax-react';
import type { AppModule, ModuleKey, NavGroup, Role } from '@radiology/ui';

/**
 * The single navigation map for the whole portal.
 *
 * This replaces four separate `roleBasedNavigation` objects that had diverged in
 * structure, casing and route conventions. Adding a screen anywhere in the
 * portal means editing this file — which is the point.
 *
 * URL scheme: /{module}/{role}/{section}
 *
 * The role segment is kept because several modules have genuinely different
 * pages for different roles (an employee dashboard and a manager dashboard are
 * not the same screen). Collapsing them onto one path would force role-
 * conditional rendering inside every page.
 *
 * Section slugs are normalised to kebab-case. Most legacy paths already match,
 * so many deep links survive unchanged; the exceptions are the camelCase admin
 * routes (`/admin/userManagement` -> `/admin/user-management`), which need
 * redirects at cutover. See UNIFIED_PORTAL_PLAN.md Phase 9.
 */

export interface ModuleDefinition {
  key: ModuleKey;
  label: string;
  description: string;
  icon: AppModule['icon'];
}

export const MODULE_DEFINITIONS: ModuleDefinition[] = [
  {
    key: 'certifications',
    label: 'Certifications',
    description: 'Staff certificates and expiry tracking',
    icon: DocumentUpload,
  },
  {
    key: 'purchasing',
    label: 'Purchase Tracking',
    description: 'Purchase requests and approvals',
    icon: ShoppingCart,
  },
  {
    key: 'attendance',
    label: 'QR Attendance',
    description: 'Training sessions and attendance',
    icon: ScanBarcode,
  },
  {
    key: 'devices',
    label: 'Medical Devices',
    description: 'Device inventory and maintenance',
    icon: Monitor,
  },
];

type ModuleNav = Partial<Record<Role, NavGroup[]>>;

const certifications: ModuleNav = {
  employee: [
    {
      key: 'main',
      items: [
        {
          key: 'dashboard',
          label: 'Dashboard',
          to: '/certifications/employee/dashboard',
          icon: Element3,
        },
        {
          key: 'certificates',
          label: 'My certificates',
          to: '/certifications/employee/certificates',
          icon: DocumentUpload,
        },
      ],
    },
  ],
  supervisor: [
    {
      key: 'review',
      label: 'Review',
      items: [
        {
          key: 'pending',
          label: 'Pending',
          to: '/certifications/supervisor/pending',
          icon: Document,
        },
        {
          key: 'approved',
          label: 'Approved',
          to: '/certifications/supervisor/approved',
          icon: TickCircle,
        },
        {
          key: 'rejected',
          label: 'Rejected',
          to: '/certifications/supervisor/rejected',
          icon: CloseCircle,
        },
      ],
    },
  ],
  // Two manager roles since the "Manager Role Enhancement & Rename" CR:
  // Operation Manager is unrestricted (BR-03), Manager is limited to its
  // assigned hospitals and sections (BR-04). Same screens — the difference is
  // enforced by RLS, not by the nav.
  'operation-manager': [
    {
      key: 'main',
      items: [
        {
          key: 'dashboard',
          label: 'Dashboard',
          to: '/certifications/operation-manager/dashboard',
          icon: Element3,
        },
      ],
    },
    {
      key: 'reports',
      label: 'Reports',
      items: [
        {
          // Still unbuilt — it was a placeholder before the manager CR and this
          // CR does not touch it. Kept rather than quietly dropped.
          key: 'security-reports',
          label: 'Security reports',
          to: '/certifications/operation-manager/security-reports',
          icon: StatusUp,
        },
        {
          key: 'certificate-reports',
          label: 'Certificate reports',
          to: '/certifications/operation-manager/certificate-reports',
          icon: Chart,
        },
      ],
    },
  ],
  manager: [
    {
      key: 'main',
      items: [
        {
          key: 'dashboard',
          label: 'Dashboard',
          to: '/certifications/manager/dashboard',
          icon: Element3,
        },
      ],
    },
    {
      key: 'reports',
      label: 'Reports',
      items: [
        {
          // Still unbuilt — it was a placeholder before the manager CR and this
          // CR does not touch it. Kept rather than quietly dropped.
          key: 'security-reports',
          label: 'Security reports',
          to: '/certifications/manager/security-reports',
          icon: StatusUp,
        },
        {
          key: 'certificate-reports',
          label: 'Certificate reports',
          to: '/certifications/manager/certificate-reports',
          icon: Chart,
        },
      ],
    },
  ],
  admin: [
    {
      key: 'certificates',
      label: 'Certificates',
      items: [
        {
          // An admin is unscoped by RLS, so this is every certificate in every
          // hospital. The legacy admin nav had no certificate view at all,
          // which left admins unable to see the data they administer.
          key: 'all-certificates',
          label: 'All certificates',
          to: '/certifications/admin/all-certificates',
          icon: DocumentUpload,
        },
        {
          key: 'overview',
          label: 'Overview',
          to: '/certifications/admin/overview',
          icon: Element3,
        },
      ],
    },
    {
      key: 'administration',
      label: 'Administration',
      items: [
        {
          key: 'user-management',
          label: 'User management',
          to: '/certifications/admin/user-management',
          icon: People,
        },
        {
          key: 'system-settings',
          label: 'System settings',
          to: '/certifications/admin/system-settings',
          icon: Setting2,
        },
        {
          key: 'audit-log',
          label: 'Audit log',
          to: '/certifications/admin/audit-log',
          icon: Personalcard,
        },
      ],
    },
  ],
};

const purchasing: ModuleNav = {
  employee: [
    {
      key: 'main',
      items: [
        {
          key: 'dashboard',
          label: 'Dashboard',
          to: '/purchasing/employee/dashboard',
          icon: Element3,
        },
        {
          key: 'purchase-request',
          label: 'Purchase request',
          to: '/purchasing/employee/purchase-request',
          icon: ClipboardText,
        },
      ],
    },
  ],
  approver: [
    {
      key: 'review',
      label: 'Review',
      items: [
        { key: 'pending', label: 'Pending', to: '/purchasing/approver/pending', icon: Document },
        {
          key: 'approved',
          label: 'Approved',
          to: '/purchasing/approver/approved',
          icon: TickCircle,
        },
        {
          key: 'rejected',
          label: 'Rejected',
          to: '/purchasing/approver/rejected',
          icon: CloseCircle,
        },
      ],
    },
  ],
  'chief-approver': [
    {
      key: 'main',
      items: [
        {
          key: 'dashboard',
          label: 'Dashboard',
          to: '/purchasing/chief-approver/dashboard',
          icon: Element3,
        },
      ],
    },
  ],
  manager: [
    {
      key: 'main',
      items: [
        {
          key: 'dashboard',
          label: 'Dashboard',
          to: '/purchasing/manager/dashboard',
          icon: Element3,
        },
      ],
    },
    {
      key: 'reports',
      label: 'Reports',
      items: [
        {
          key: 'approver-reports',
          label: 'Approver reports',
          to: '/purchasing/manager/approver-reports',
          icon: StatusUp,
        },
        {
          key: 'purchase-reports',
          label: 'Purchase reports',
          to: '/purchasing/manager/purchase-reports',
          icon: Chart,
        },
      ],
    },
  ],
  admin: [
    // The legacy admin nav had no request view at all — an admin could
    // administer the module but never see its data. Same gap as certifications,
    // closed the same way.
    {
      key: 'main',
      items: [
        {
          key: 'all-requests',
          label: 'All requests',
          to: '/purchasing/admin/all-requests',
          icon: ClipboardText,
        },
        {
          key: 'overview',
          label: 'Overview',
          to: '/purchasing/admin/overview',
          icon: Chart,
        },
      ],
    },
    {
      key: 'administration',
      label: 'Administration',
      items: [
        {
          key: 'user-management',
          label: 'User management',
          to: '/purchasing/admin/user-management',
          icon: People,
        },
        {
          key: 'system-settings',
          label: 'System settings',
          to: '/purchasing/admin/system-settings',
          icon: Setting2,
        },
        {
          key: 'audit-log',
          label: 'Audit log',
          to: '/purchasing/admin/audit-log',
          icon: Personalcard,
        },
      ],
    },
  ],
};

const attendance: ModuleNav = {
  secretary: [
    {
      key: 'main',
      items: [
        {
          key: 'dashboard',
          label: 'Dashboard',
          to: '/attendance/secretary/dashboard',
          icon: Element3,
        },
        {
          key: 'create-session',
          label: 'Add session',
          to: '/attendance/secretary/create-session',
          icon: Calendar,
        },
        {
          key: 'archive',
          label: 'Archive',
          to: '/attendance/secretary/archive',
          icon: Archive,
        },
      ],
    },
  ],
  manager: [
    {
      key: 'main',
      items: [
        {
          key: 'dashboard',
          label: 'Dashboard',
          to: '/attendance/manager/dashboard',
          icon: Element3,
        },
      ],
    },
  ],
  admin: [
    {
      key: 'administration',
      label: 'Administration',
      items: [
        {
          key: 'user-management',
          label: 'User management',
          to: '/attendance/admin/user-management',
          icon: People,
        },
        {
          key: 'system-settings',
          label: 'System settings',
          to: '/attendance/admin/system-settings',
          icon: Setting2,
        },
      ],
    },
  ],
};

const devices: ModuleNav = {
  secretary: [
    {
      key: 'inventory',
      label: 'Inventory',
      items: [
        {
          key: 'all-devices',
          label: 'All devices',
          to: '/devices/secretary/all-devices',
          icon: Box1,
        },
        { key: 'new-device', label: 'Add device', to: '/devices/secretary/new-device', icon: Cpu },
        {
          key: 'maintenance-schedule',
          label: 'Maintenance',
          to: '/devices/secretary/maintenance-schedule',
          icon: Task,
        },
      ],
    },
  ],
  // `manager`, not `management`. Verified against the medical-devices Keycloak
  // client, which grants admin / manager / secretary. The legacy route path was
  // /management/dashboard, which is where the wrong name came from — 2 users
  // hold this role and would otherwise have landed on "no access".
  manager: [
    {
      key: 'main',
      items: [
        {
          key: 'dashboard',
          label: 'Dashboard',
          to: '/devices/manager/dashboard',
          icon: Element3,
        },
      ],
    },
  ],
  admin: [
    {
      key: 'main',
      items: [
        { key: 'dashboard', label: 'Dashboard', to: '/devices/admin/dashboard', icon: Element3 },
        {
          key: 'all-devices',
          label: 'All devices',
          to: '/devices/admin/all-devices',
          icon: Box1,
        },
      ],
    },
    {
      key: 'administration',
      label: 'Administration',
      items: [
        {
          key: 'user-management',
          label: 'User management',
          to: '/devices/admin/user-management',
          icon: People,
        },
        {
          key: 'system-settings',
          label: 'System settings',
          to: '/devices/admin/system-settings',
          icon: Setting2,
        },
        {
          key: 'audit-log',
          label: 'Audit log',
          to: '/devices/admin/audit-log',
          icon: Personalcard,
        },
      ],
    },
  ],
};

export const MODULE_NAVIGATION: Record<ModuleKey, ModuleNav> = {
  certifications,
  purchasing,
  attendance,
  devices,
};

/** Nav groups for a module+role, or [] when the role has no screens there. */
export function navigationFor(module: ModuleKey, role: Role | undefined): NavGroup[] {
  if (!role) return [];
  return MODULE_NAVIGATION[module][role] ?? [];
}

/** Where to send the user when they open a module: its first nav item. */
export function landingRoute(module: ModuleKey, role: Role | undefined): string | null {
  const groups = navigationFor(module, role);
  return groups[0]?.items[0]?.to ?? null;
}

/** Every route the map knows about, used to validate access. */
export function allRoutesFor(module: ModuleKey, role: Role | undefined): string[] {
  return navigationFor(module, role).flatMap(group => group.items.map(item => item.to));
}
